import React, { useState, useRef, useEffect } from 'react';
import { Building, Plus, Search, Loader2, CheckCircle, X, ExternalLink, ChevronDown, ArrowRight, ArrowLeft, User, Mail, Shield } from 'lucide-react';
import { Company, CompanySize } from '../../types';
import { assetsApi, BillingPlan, CompaniesHouseResult } from '../../domains/admin/api/api';
import { usersApi, ApiError } from '../../domains/admin/api/users.api';
import FormModal from './FormModal';

export interface CreateCompanyModalProps {
  onClose: () => void;
  onSubmit: (data: any) => void | Promise<void>;
}

const INDUSTRY_OPTIONS = [
  'Manufacturing', 'Energy', 'Transportation', 'Agriculture',
  'Construction', 'Financial Services', 'Healthcare', 'Technology',
  'Retail', 'General',
];

const inputCls = 'w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm';
const labelCls = 'block text-sm font-medium text-slate-700';
const formatPlanLabel = (plan: BillingPlan) => {
  const hasPrice = typeof plan.price === 'number';
  const currency = plan.currency || 'USD';
  const billingCycle = plan.billing_cycle || 'month';
  const pricePart = hasPrice ? `${plan.price} ${currency}/${billingCycle}` : 'Custom pricing';
  return `${plan.name} (${pricePart})`;
};

export const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({ onClose, onSubmit }) => {
  // Form state — controlled so Companies House can populate fields
  const [form, setForm] = useState({
    name: '', legal_name: '', industry: '', size: '',
    registration_number: '', address: '', phone: '', email: '', website: '', plan_id: '',
  });
  const [billingPlans, setBillingPlans] = useState<BillingPlan[]>([]);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState<string | null>(null);

  // Companies House lookup state
  const [chQuery, setChQuery] = useState('');
  const [chResults, setChResults] = useState<CompaniesHouseResult[]>([]);
  const [chSearching, setChSearching] = useState(false);
  const [chPopulating, setChPopulating] = useState(false);
  const [chSource, setChSource] = useState<string | null>(null); // company number used to populate
  const [chError, setChError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close results dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBillingLoading(true);
      setBillingError(null);
      try {
        const plans = await assetsApi.getBillingPlans();
        if (!alive) return;
        setBillingPlans(plans);
        if (plans.length > 0) {
          setForm(prev => (prev.plan_id ? prev : { ...prev, plan_id: plans[0].id }));
        } else {
          setBillingError('No active billing plans are available. Please create a plan first.');
        }
      } catch (err: any) {
        if (!alive) return;
        setBillingError(err?.response?.data?.message || err?.message || 'Failed to load billing plans.');
      } finally {
        if (alive) setBillingLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleChQueryChange = (value: string) => {
    setChQuery(value);
    setChError(null);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (value.trim().length < 2) { setChResults([]); setShowResults(false); return; }
    searchDebounce.current = setTimeout(() => runChSearch(value), 400);
  };

  const runChSearch = async (query: string) => {
    setChSearching(true);
    setShowResults(true);
    try {
      const res = await assetsApi.searchCompaniesHouse(query);
      if (!res.configured) {
        setChError('Companies House is not configured on this server. Fill the form manually or ask your administrator to add COMPANIES_HOUSE_API_KEY.');
        setChResults([]);
      } else {
        setChResults(res.data || []);
        if ((res.data || []).length === 0) setChError('No companies found. Try a different name or company number.');
        else setChError(null);
      }
    } catch {
      setChError('Search failed. Fill the form manually or try again.');
      setChResults([]);
    } finally {
      setChSearching(false);
    }
  };

  const handleSelectResult = async (result: CompaniesHouseResult) => {
    setShowResults(false);
    setChPopulating(true);
    setChError(null);
    try {
      const res = await assetsApi.getCompaniesHouseProfile(result.company_number);
      if (res.success && res.data?.mapped) {
        const m = res.data.mapped;
        setForm(prev => ({
          ...prev,
          name: m.name || prev.name,
          legal_name: m.legal_name || prev.legal_name,
          registration_number: m.registration_number || prev.registration_number,
          address: m.address || prev.address,
          industry: m.industry || prev.industry,
        }));
        setChSource(result.company_number);
      }
    } catch (err: any) {
      setChError(err?.message || 'Failed to load company details.');
    } finally {
      setChPopulating(false);
    }
  };

  const clearChSource = () => {
    setChSource(null);
    setChQuery('');
    setChResults([]);
  };

  const handleFieldChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(form);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status: string) => {
    const s = status?.toLowerCase();
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'dissolved' || s === 'liquidation') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-600';
  };

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-4 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
            <Plus className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Create New Company</h3>
            <p className="text-emerald-100 text-sm mt-0.5">Add a new company to the system</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white p-6 space-y-5 max-h-[70vh] overflow-y-auto">

        {/* ── Companies House Lookup ─────────────────────────────────── */}
        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Building size={16} className="text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">Auto-populate from Companies House</span>
            <span className="ml-auto text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full font-medium">Optional</span>
          </div>

          {chSource ? (
            <div className="flex items-center gap-2 bg-white border border-emerald-300 rounded-xl px-4 py-3">
              <CheckCircle size={16} className="text-emerald-600 shrink-0" />
              <span className="text-sm text-emerald-800 font-medium flex-1">
                Populated from Companies House — <span className="font-mono text-xs">{chSource}</span>
              </span>
              <button onClick={clearChSource} className="text-slate-400 hover:text-slate-600 transition-colors" title="Clear">
                <X size={16} />
              </button>
            </div>
          ) : (
            <div ref={searchRef} className="relative">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={chQuery}
                  onChange={e => handleChQueryChange(e.target.value)}
                  onFocus={() => chResults.length > 0 && setShowResults(true)}
                  placeholder="Search by company name or registration number..."
                  className="w-full pl-9 pr-10 py-2.5 border border-emerald-300 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
                {chSearching || chPopulating ? (
                  <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500 animate-spin" />
                ) : chQuery ? (
                  <button onClick={() => { setChQuery(''); setChResults([]); setShowResults(false); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                ) : null}
              </div>

              {/* Results dropdown */}
              {showResults && (chResults.length > 0 || chError) && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-64 overflow-y-auto">
                  {chError && (
                    <div className="px-4 py-3 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">{chError}</div>
                  )}
                  {chResults.map(r => (
                    <button
                      key={r.company_number}
                      onClick={() => handleSelectResult(r)}
                      className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors border-b border-slate-100 last:border-0 group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-emerald-700">{r.company_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500 font-mono">{r.company_number}</span>
                            {r.registered_office_address?.locality && (
                              <span className="text-xs text-slate-400">· {r.registered_office_address.locality}</span>
                            )}
                          </div>
                        </div>
                        <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusBadge(r.company_status)}`}>
                          {r.company_status || 'unknown'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {chError && !showResults && (
            <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">{chError}</p>
          )}

          <p className="text-xs text-emerald-700">
            Searches the UK Companies House register. Results auto-fill the form below — you can edit any field before saving.
          </p>
        </div>

        {/* ── Company Form ───────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className={labelCls}>Company Name *</label>
            <input type="text" value={form.name} onChange={e => handleFieldChange('name', e.target.value)}
              required className={inputCls} placeholder="e.g., Tech Solutions Ltd" />
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Legal Name *</label>
            <input type="text" value={form.legal_name} onChange={e => handleFieldChange('legal_name', e.target.value)}
              required className={inputCls} placeholder="e.g., Tech Solutions Limited" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className={labelCls}>Industry *</label>
            <div className="relative">
              <select value={form.industry} onChange={e => handleFieldChange('industry', e.target.value)}
                required className={`${inputCls} appearance-none pr-9 cursor-pointer bg-white`}>
                <option value="">Select Industry</option>
                {INDUSTRY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Company Size *</label>
            <div className="relative">
              <select value={form.size} onChange={e => handleFieldChange('size', e.target.value)}
                required className={`${inputCls} appearance-none pr-9 cursor-pointer bg-white`}>
                <option value="">Select Size</option>
                <option value="small">Small (1–50 employees)</option>
                <option value="medium">Medium (51–250 employees)</option>
                <option value="large">Large (251–1000 employees)</option>
                <option value="enterprise">Enterprise (1000+ employees)</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <label className={labelCls}>Subscription Plan *</label>
          <div className="relative">
            <select
              value={form.plan_id}
              onChange={e => handleFieldChange('plan_id', e.target.value)}
              disabled={billingLoading || billingPlans.length === 0}
              className={`${inputCls} appearance-none pr-9 cursor-pointer bg-white disabled:bg-slate-100`}
            >
              <option value="">{billingLoading ? 'Loading plans...' : 'Select a billing plan'}</option>
              {billingPlans.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {formatPlanLabel(plan)}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          {billingError && <p className="text-xs text-red-600">{billingError}</p>}
        </div>

        <div className="space-y-1.5">
          <label className={labelCls}>Registration Number</label>
          <input type="text" value={form.registration_number} onChange={e => handleFieldChange('registration_number', e.target.value)}
            className={inputCls} placeholder="e.g., 12345678" />
        </div>

        <div className="space-y-1.5">
          <label className={labelCls}>Registered Address</label>
          <textarea value={form.address} onChange={e => handleFieldChange('address', e.target.value)}
            rows={2}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-sm resize-none"
            placeholder="Full company address" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className={labelCls}>Phone</label>
            <input type="tel" value={form.phone} onChange={e => handleFieldChange('phone', e.target.value)}
              className={inputCls} placeholder="+44 20 1234 5678" />
          </div>
          <div className="space-y-1.5">
            <label className={labelCls}>Email</label>
            <input type="email" value={form.email} onChange={e => handleFieldChange('email', e.target.value)}
              className={inputCls} placeholder="contact@company.com" />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className={labelCls}>Website</label>
          <input type="url" value={form.website} onChange={e => handleFieldChange('website', e.target.value)}
            className={inputCls} placeholder="https://www.company.com" />
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex justify-end gap-3">
        <button onClick={onClose} disabled={submitting}
          className="px-5 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-100 transition-colors text-sm disabled:opacity-50">
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || billingLoading || !form.name || !form.legal_name || !form.industry || !form.size || !form.plan_id}
          className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-500 hover:to-teal-500 transition-all shadow-md disabled:opacity-50 text-sm"
        >
          {submitting ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
          {submitting ? 'Creating...' : 'Create Company'}
        </button>
      </div>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   CREATE COMPANY WIZARD  (2-step: company details → company admin setup)
   ══════════════════════════════════════════════════════════════════════════ */

export interface CreateCompanyWizardProps {
  onClose: () => void;
  onComplete: (companyId: number) => void;
}

export const CreateCompanyWizard: React.FC<CreateCompanyWizardProps> = ({ onClose, onComplete }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [companyForm, setCompanyForm] = useState({
    name: '', legal_name: '', industry: '', size: '',
    registration_number: '', address: '', phone: '', email: '', website: '', plan_id: '',
  });
  const [billingPlans, setBillingPlans] = useState<BillingPlan[]>([]);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [adminForm, setAdminForm] = useState({ first_name: '', last_name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [createdCompanyId, setCreatedCompanyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflictUser, setConflictUser] = useState<{ name: string; company_name: string | null; is_active: boolean } | null>(null);

  // Companies House lookup (reuse same logic as CreateCompanyModal)
  const [chQuery, setChQuery] = useState('');
  const [chResults, setChResults] = useState<CompaniesHouseResult[]>([]);
  const [chSearching, setChSearching] = useState(false);
  const [chPopulating, setChPopulating] = useState(false);
  const [chSource, setChSource] = useState<string | null>(null);
  const [chError, setChError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBillingLoading(true);
      setBillingError(null);
      try {
        const plans = await assetsApi.getBillingPlans();
        if (!alive) return;
        setBillingPlans(plans);
        if (plans.length > 0) {
          setCompanyForm(prev => (prev.plan_id ? prev : { ...prev, plan_id: plans[0].id }));
        } else {
          setBillingError('No active billing plans are available. Please create a plan first.');
        }
      } catch (err: any) {
        if (!alive) return;
        setBillingError(err?.response?.data?.message || err?.message || 'Failed to load billing plans.');
      } finally {
        if (alive) setBillingLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleChQuery = (value: string) => {
    setChQuery(value);
    setChError(null);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (value.trim().length < 2) { setChResults([]); setShowResults(false); return; }
    searchDebounce.current = setTimeout(async () => {
      setChSearching(true); setShowResults(true);
      try {
        const res = await assetsApi.searchCompaniesHouse(value);
        if (!res.configured) { setChError('Companies House is not configured on this server. Fill the form manually.'); setChResults([]); }
        else { setChResults(res.data || []); if (!(res.data || []).length) setChError('No companies found. Try a different search.'); }
      } catch { setChError('Search failed. Fill the form manually or try again.'); setChResults([]); }
      finally { setChSearching(false); }
    }, 400);
  };

  const handleSelectCh = async (result: CompaniesHouseResult) => {
    setShowResults(false); setChPopulating(true);
    try {
      const res = await assetsApi.getCompaniesHouseProfile(result.company_number);
      if (res.success && res.data?.mapped) {
        const m = res.data.mapped;
        setCompanyForm(prev => ({
          ...prev,
          name: m.name || prev.name,
          legal_name: m.legal_name || prev.legal_name,
          registration_number: m.registration_number || prev.registration_number,
          address: m.address || prev.address,
          industry: m.industry || prev.industry,
        }));
        setChSource(result.company_number);
      }
    } catch { setChError('Failed to load details.'); }
    finally { setChPopulating(false); }
  };

  const handleNext = () => {
    if (!companyForm.name || !companyForm.legal_name || !companyForm.industry || !companyForm.size || !companyForm.plan_id) {
      setError('Please fill in all required fields.');
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleFinish = async (skipAdmin: boolean) => {
    setSubmitting(true);
    setError(null);
    setConflictUser(null);
    try {
      // Create (or reuse already-created) company
      let newCompanyId = createdCompanyId;
      if (!newCompanyId) {
        const result = await assetsApi.createCompany(companyForm as any);
        newCompanyId = result.id;
        setCreatedCompanyId(newCompanyId);
      }

      // Optionally create company admin
      if (!skipAdmin && adminForm.first_name && adminForm.last_name && adminForm.email) {
        const profiles = await usersApi.getUserTypes(false);
        const companyAdminProfile = profiles.find((p: any) => p.name === 'Company Admin');

        await usersApi.createUser({
          email: adminForm.email,
          first_name: adminForm.first_name,
          last_name: adminForm.last_name,
          company_id: newCompanyId,
          user_types: companyAdminProfile ? [companyAdminProfile.id] : [],
          permissionKeys: [],
        });
      }

      onComplete(newCompanyId);
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 409 && err.data?.existingUser) {
        const eu = err.data.existingUser;
        setConflictUser(eu);
        setError(err.message);
      } else {
        setError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (status: string) => {
    const s = status?.toLowerCase();
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'dissolved' || s === 'liquidation') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-600';
  };

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-6 py-4 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Building className="text-white" size={22} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Create New Company</h3>
              <p className="text-purple-200 text-sm mt-0.5">
                Step {step} of 2 — {step === 1 ? 'Company Details' : 'Company Admin Setup'}
              </p>
            </div>
          </div>
          {/* Step progress */}
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 1 ? 'bg-white text-purple-700' : 'bg-white/20 text-white'
            }`}>1</div>
            <div className={`w-8 h-1 rounded ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step >= 2 ? 'bg-white text-purple-700' : 'bg-white/20 text-white'
            }`}>2</div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white p-6 space-y-5 max-h-[65vh] overflow-y-auto">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 font-medium space-y-1">
            <p>{error}</p>
            {conflictUser && (
              <p className="text-red-600 font-normal">
                <span className="font-semibold">{conflictUser.name}</span>
                {conflictUser.company_name ? ` — ${conflictUser.company_name}` : ' — system-level user'}
                {!conflictUser.is_active && ' (inactive)'}
                {'. '}Use a different email address or skip admin setup.
              </p>
            )}
          </div>
        )}

        {/* ── STEP 1: Company Details ── */}
        {step === 1 && (
          <>
            {/* Companies House lookup */}
            <div className="rounded-2xl border-2 border-purple-200 bg-purple-50/60 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Building size={15} className="text-purple-600" />
                <span className="text-sm font-semibold text-purple-800">Auto-populate from Companies House</span>
                <span className="ml-auto text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full font-medium">Optional</span>
              </div>
              {chSource ? (
                <div className="flex items-center gap-2 bg-white border border-purple-300 rounded-xl px-4 py-3">
                  <CheckCircle size={15} className="text-purple-600 shrink-0" />
                  <span className="text-sm text-purple-800 font-medium flex-1">
                    Populated — <span className="font-mono text-xs">{chSource}</span>
                  </span>
                  <button onClick={() => { setChSource(null); setChQuery(''); setChResults([]); }}
                    className="text-slate-400 hover:text-slate-600">
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <div ref={searchRef} className="relative">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input type="text" value={chQuery} onChange={e => handleChQuery(e.target.value)}
                      onFocus={() => chResults.length > 0 && setShowResults(true)}
                      placeholder="Search by company name or number..."
                      className="w-full pl-9 pr-10 py-2.5 border border-purple-300 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
                    {chSearching || chPopulating
                      ? <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 animate-spin" />
                      : chQuery
                        ? <button onClick={() => { setChQuery(''); setChResults([]); setShowResults(false); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            <X size={13} />
                          </button>
                        : null}
                  </div>
                  {showResults && (chResults.length > 0 || chError) && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-56 overflow-y-auto">
                      {chError && <div className="px-4 py-3 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">{chError}</div>}
                      {chResults.map(r => (
                        <button key={r.company_number} onClick={() => handleSelectCh(r)}
                          className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b border-slate-100 last:border-0 group">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-purple-700">{r.company_name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-slate-500 font-mono">{r.company_number}</span>
                                {r.registered_office_address?.locality && (
                                  <span className="text-xs text-slate-400">· {r.registered_office_address.locality}</span>
                                )}
                              </div>
                            </div>
                            <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusBadge(r.company_status)}`}>
                              {r.company_status || 'unknown'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {chError && !showResults && (
                <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">{chError}</p>
              )}
            </div>

            {/* Company Form */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Company Name *</label>
                <input type="text" value={companyForm.name}
                  onChange={e => setCompanyForm(f => ({ ...f, name: e.target.value }))}
                  className={inputCls} placeholder="e.g., Tech Solutions Ltd" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Legal Name *</label>
                <input type="text" value={companyForm.legal_name}
                  onChange={e => setCompanyForm(f => ({ ...f, legal_name: e.target.value }))}
                  className={inputCls} placeholder="e.g., Tech Solutions Limited" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Industry *</label>
                <div className="relative">
                  <select value={companyForm.industry}
                    onChange={e => setCompanyForm(f => ({ ...f, industry: e.target.value }))}
                    className={`${inputCls} appearance-none pr-9 cursor-pointer bg-white`}>
                    <option value="">Select Industry</option>
                    {INDUSTRY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Company Size *</label>
                <div className="relative">
                  <select value={companyForm.size}
                    onChange={e => setCompanyForm(f => ({ ...f, size: e.target.value }))}
                    className={`${inputCls} appearance-none pr-9 cursor-pointer bg-white`}>
                    <option value="">Select Size</option>
                    <option value="small">Small (1–50)</option>
                    <option value="medium">Medium (51–250)</option>
                    <option value="large">Large (251–1000)</option>
                    <option value="enterprise">Enterprise (1000+)</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={labelCls}>Subscription Plan *</label>
              <div className="relative">
                <select
                  value={companyForm.plan_id}
                  onChange={e => setCompanyForm(f => ({ ...f, plan_id: e.target.value }))}
                  disabled={billingLoading || billingPlans.length === 0}
                  className={`${inputCls} appearance-none pr-9 cursor-pointer bg-white disabled:bg-slate-100`}
                >
                  <option value="">{billingLoading ? 'Loading plans...' : 'Select billing plan'}</option>
                  {billingPlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {formatPlanLabel(plan)}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
              {billingError && <p className="text-xs text-red-600">{billingError}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Registration Number</label>
                <input type="text" value={companyForm.registration_number}
                  onChange={e => setCompanyForm(f => ({ ...f, registration_number: e.target.value }))}
                  className={inputCls} placeholder="e.g., 12345678" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Email</label>
                <input type="email" value={companyForm.email}
                  onChange={e => setCompanyForm(f => ({ ...f, email: e.target.value }))}
                  className={inputCls} placeholder="contact@company.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={labelCls}>Phone</label>
                <input type="tel" value={companyForm.phone}
                  onChange={e => setCompanyForm(f => ({ ...f, phone: e.target.value }))}
                  className={inputCls} placeholder="+44 20 1234 5678" />
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Website</label>
                <input type="url" value={companyForm.website}
                  onChange={e => setCompanyForm(f => ({ ...f, website: e.target.value }))}
                  className={inputCls} placeholder="https://www.company.com" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={labelCls}>Registered Address</label>
              <textarea value={companyForm.address}
                onChange={e => setCompanyForm(f => ({ ...f, address: e.target.value }))}
                rows={2}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm resize-none"
                placeholder="Full company address" />
            </div>
          </>
        )}

        {/* ── STEP 2: Company Admin Setup ── */}
        {step === 2 && (
          <>
            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <Shield size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-purple-800">Set Up Company Admin</p>
                  <p className="text-xs text-purple-600 mt-1 leading-relaxed">
                    The Company Admin will manage this company's users, compliance and settings.
                    A welcome email with login credentials will be sent automatically.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={labelCls}>First Name *</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input type="text" value={adminForm.first_name}
                    onChange={e => setAdminForm(f => ({ ...f, first_name: e.target.value }))}
                    className={`${inputCls} pl-9`} placeholder="Jane" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className={labelCls}>Last Name *</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input type="text" value={adminForm.last_name}
                    onChange={e => setAdminForm(f => ({ ...f, last_name: e.target.value }))}
                    className={`${inputCls} pl-9`} placeholder="Smith" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={labelCls}>Email Address *</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input type="email" value={adminForm.email}
                  onChange={e => { setAdminForm(f => ({ ...f, email: e.target.value })); setError(null); setConflictUser(null); }}
                  className={`${inputCls} pl-9 ${conflictUser ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="jane.smith@company.com" />
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-500">
              You can also skip this step and add users later from the company detail page.
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex items-center justify-between gap-3">
        <button onClick={step === 1 ? onClose : () => setStep(1)}
          disabled={submitting}
          className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-300 text-slate-600 font-medium rounded-xl hover:bg-slate-100 transition-colors text-sm disabled:opacity-50">
          {step === 2 && <ArrowLeft size={14} />}
          {step === 1 ? 'Cancel' : 'Back'}
        </button>
        <div className="flex items-center gap-2">
          {step === 2 && (
            <button onClick={() => handleFinish(true)} disabled={submitting}
              className="px-4 py-2.5 border border-slate-300 text-slate-600 font-medium rounded-xl hover:bg-slate-100 transition-colors text-sm disabled:opacity-50">
              Skip Admin Setup
            </button>
          )}
          {step === 1 ? (
            <button onClick={handleNext}
              disabled={billingLoading || !companyForm.name || !companyForm.legal_name || !companyForm.industry || !companyForm.size || !companyForm.plan_id}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-500 hover:to-indigo-500 transition-all shadow-md disabled:opacity-50 text-sm">
              Next: Admin Setup <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={() => handleFinish(false)}
              disabled={submitting || !adminForm.first_name || !adminForm.last_name || !adminForm.email}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-500 hover:to-indigo-500 transition-all shadow-md disabled:opacity-50 text-sm">
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
              {submitting ? 'Creating…' : 'Create Company & Admin'}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export interface CompanyDetailsModalProps {
  company: Company;
  onClose: () => void;
}

export const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({ company, onClose }) => {
  const getIndustryColor = (industry: string) => {
    const colors: Record<string, string> = {
      'Manufacturing': 'bg-blue-100 text-blue-800 border-blue-200',
      'Energy': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Transportation': 'bg-green-100 text-green-800 border-green-200',
      'Agriculture': 'bg-orange-100 text-orange-800 border-orange-200',
      'Construction': 'bg-slate-100 text-slate-800 border-slate-200',
      'Financial Services': 'bg-purple-100 text-purple-800 border-purple-200',
      'Healthcare': 'bg-red-100 text-red-800 border-red-200',
      'Technology': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'Retail': 'bg-pink-100 text-pink-800 border-pink-200',
      'General': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[industry] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getSizeColor = (size: CompanySize) => {
    const colors: Record<CompanySize, string> = {
      'small': 'bg-emerald-100 text-emerald-800',
      'medium': 'bg-amber-100 text-amber-800',
      'large': 'bg-orange-100 text-orange-800',
      'enterprise': 'bg-red-100 text-red-800'
    };
    return colors[size] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-600 to-slate-700 px-6 py-4 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
            <Building className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Company Details</h3>
            <p className="text-slate-100 text-sm mt-0.5">View company information</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Company Name
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              {company.name}
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Legal Name
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              {company.legal_name}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Industry
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getIndustryColor(company.industry)}`}>
                {company.industry}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Company Size
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSizeColor(company.size)}`}>
                {company.size}
              </span>
            </div>
          </div>
        </div>

        {company.registration_number && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Registration Number
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              {company.registration_number}
            </div>
          </div>
        )}

        {company.address && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Address
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              {company.address}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {company.phone && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Phone
              </label>
              <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
                {company.phone}
              </div>
            </div>
          )}
          {company.email && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Email
              </label>
              <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
                {company.email}
              </div>
            </div>
          )}
        </div>

        {company.website && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Website
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              <a href={company.website} target="_blank" rel="noopener noreferrer"
                 className="text-sm font-medium text-blue-600 hover:text-blue-700">
                {company.website}
              </a>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Status
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                company.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {company.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Created
            </label>
            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
              {new Date(company.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Updated
          </label>
          <div className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium text-slate-900">
            {new Date(company.updated_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-slate-50 rounded-b-2xl flex justify-end">
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
        >
          Close
        </button>
      </div>
    </>
  );
};

export interface EditCompanyModalProps {
  company: Company;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

export const EditCompanyModal: React.FC<EditCompanyModalProps> = ({ company, onClose, onSubmit }) => {
  const [formData, setFormData] = React.useState({
    name: company.name || '',
    legal_name: company.legal_name || '',
    industry: company.industry || '',
    size: company.size || '',
    registration_number: company.registration_number || '',
    address: company.address || '',
    phone: company.phone || '',
    email: company.email || '',
    website: company.website || '',
    is_active: company.is_active || false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(formData);
      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  return (
    <FormModal
      title="Edit Company"
      subtitle="Update company information"
      icon={<Building className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText="Update Company"
      cancelText="Cancel"
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-slate-700">
            Company Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="e.g., Tech Solutions Inc."
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="legal_name" className="block text-sm font-medium text-slate-700">
            Legal Name *
          </label>
          <input
            type="text"
            id="legal_name"
            name="legal_name"
            value={formData.legal_name}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="e.g., Tech Solutions Inc. Ltd."
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="industry" className="block text-sm font-medium text-slate-700">
            Industry *
          </label>
          <select
            id="industry"
            name="industry"
            value={formData.industry}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
          >
            <option value="">Select Industry</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Energy">Energy</option>
            <option value="Transportation">Transportation</option>
            <option value="Agriculture">Agriculture</option>
            <option value="Construction">Construction</option>
            <option value="Financial Services">Financial Services</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Technology">Technology</option>
            <option value="Retail">Retail</option>
            <option value="General">General</option>
          </select>
        </div>
        <div className="space-y-2">
          <label htmlFor="size" className="block text-sm font-medium text-slate-700">
            Company Size *
          </label>
          <select
            id="size"
            name="size"
            value={formData.size}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
          >
            <option value="">Select Size</option>
            <option value="small">Small (1-50 employees)</option>
            <option value="medium">Medium (51-250 employees)</option>
            <option value="large">Large (251-1000 employees)</option>
            <option value="enterprise">Enterprise (1000+ employees)</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="registration_number" className="block text-sm font-medium text-slate-700">
          Registration Number
        </label>
        <input
          type="text"
          id="registration_number"
          name="registration_number"
          value={formData.registration_number}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
          placeholder="Company registration number"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="address" className="block text-sm font-medium text-slate-700">
          Address
        </label>
        <input
          type="text"
          id="address"
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
          placeholder="Company address"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="+1 (555) 123-4567"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="contact@company.com"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="website" className="block text-sm font-medium text-slate-700">
          Website
        </label>
        <input
          type="url"
          id="website"
          name="website"
          value={formData.website}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
          placeholder="https://www.company.com"
        />
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleInputChange}
            className="rounded border-slate-300 text-slate-600 focus:ring-slate-500"
          />
          <span className="text-sm font-medium text-slate-700">Company is active</span>
        </label>
      </div>
    </FormModal>
  );
};
